import { CardSkeleton } from "@/components/skeletons";
import { X, Check, Edit2, FileText } from "lucide-react";
import { ProfileData } from "@/hooks/useProfile";
import { Card } from "@/components/Card";
import { useEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import { Textarea } from "@/components/Textarea";
import { Text } from "@/components/Typography";
import { FormattedText } from "@/components/FormattedText";

interface ProfileUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

interface BCardProps {
  user?: ProfileUser | null;
  profile?: ProfileData | null;
  isLoading?: boolean;
  onSave?: (bio: string) => Promise<void> | void;
  onCancel?: () => void;
  isSaving?: boolean;
  editable?: boolean;
}

/** Shared with the Expert signup's Bio field, so a bio written there can be
 *  edited here without first being cut down to fit. */
export const BIO_MIN_LENGTH = 150;
export const BIO_MAX_LENGTH = 500;
const bioMinLength = BIO_MIN_LENGTH;
const bioMaxLength = BIO_MAX_LENGTH;

export const BioCard = ({
  profile,
  isLoading = false,
  onSave,
  onCancel,
  isSaving = false,
  editable = true,
}: BCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bio, setBio] = useState(profile?.bio ?? "");
  const [localSaving, setLocalSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // sync profile changes
  useEffect(() => {
    setBio(profile?.bio ?? "");
  }, [profile?.bio]);

  // focus textarea when editing
  useEffect(() => {
    if (isEditing) {
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [isEditing]);

  if (isLoading)
    return (
      <Card title="Bio">
        <CardSkeleton withHeader contentLines={4} />
      </Card>
    );

  const charCount = bio.length;
  const isDirty = (profile?.bio ?? "") !== bio;
  const isValidLength = charCount >= bioMinLength && charCount <= bioMaxLength;

  const handleEdit = () => {
    // ensure we start with the latest profile bio
    setBio(profile?.bio ?? "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Reset to original values
    setBio(profile?.bio ?? "");
    setIsEditing(false);
    if (onCancel) onCancel();
  };

  const onSubmit = async () => {
    if (!onSave) return;

    setLocalSaving(true);
    try {
      await onSave(bio || "");
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save bio", error);
    } finally {
      setLocalSaving(false);
    }
  };

  const saveDisabled =
    !isValidLength || (!isDirty && !isEditing) || isSaving || localSaving;

  return (
    <Card className="w-full rounded-[12px] border border-neutral-100 dark:border-surface-border bg-white dark:bg-surface-card p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <FileText className="font-bold text-primary-700" />
          <h3
            className="font-inter font-bold text-neutral-900 dark:text-white"
            style={{
              fontSize: "var(--profile-section-title-font-size)",
              lineHeight: "var(--profile-section-title-line-height)",
            }}
          >
            Bio
          </h3>
        </div>

        {/* Right side: Edit or Save/Cancel */}
        {isEditing && editable ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 rounded-[10px] border border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card text-neutral-900 dark:text-white font-inter text-[13.2px] font-normal leading-5 transition-all hover:border-neutral-300 dark:hover:border-surface-border hover:bg-neutral-50 dark:hover:bg-surface-card-inner disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving || localSaving}
              aria-label="Cancel editing bio"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>

            <Button
              variant="primaryGradient"
              onClick={onSubmit}
              disabled={saveDisabled}
              className="flex items-center gap-2 px-4 py-2 rounded-[10px] text-white font-inter text-[13.2px] font-normal leading-5 transition-all disabled:opacity-50 disabled:cursor-not-allowed "
              aria-label="Save bio"
            >
              <Check className="h-4 w-4" />
              {isSaving || localSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        ) : editable ? (
          <Button
            variant="ghost"
            onClick={handleEdit}
            className="group flex h-9 w-full items-center justify-center gap-2 self-start rounded-[10px] border border-neutral-200 dark:border-surface-border bg-neutral-50 dark:bg-surface-card px-3 py-2 transition-all hover:border-neutral-300 dark:hover:border-surface-border hover:bg-neutral-100 dark:hover:bg-surface-card-inner hover:shadow-sm sm:w-auto"
            aria-label="Edit bio"
          >
            <Edit2 className="h-4 w-4 text-gray-500 dark:text-pab-dark-subtle-text" />
            <Text
              as="span"
              className="font-inter text-[13.2px] font-normal leading-5 text-neutral-900 dark:text-white"
            >
              Edit
            </Text>
          </Button>
        ) : null}
      </div>

      {/* content */}
      {!isEditing ? (
        <Text
          className="font-instrument font-normal text-neutral-900 dark:text-pab-dark-heading-text whitespace-pre-line"
          style={{
            fontSize: "var(--profile-bio-content-font-size)",
            lineHeight: "var(--profile-bio-content-line-height)",
          }}
        >
          {profile?.bio && profile.bio.length > 0
            ? <FormattedText text={profile.bio} />
            : "You have not added your bio yet. Please add your bio."}
        </Text>
      ) : (
        <div className="flex w-full flex-col">
          <Textarea
            id="bio"
            ref={textareaRef}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="bg-neutral-50 rounded-lg p-3 text-neutral-900 font-instrument text-sm leading-6 resize-vertical focus:outline-none focus:ring-2 ring-primary-300"
            rows={4}
            richText
            placeholder="Enter bio"
            maxLength={bioMaxLength}
          />
          
          {/* Character counter at right end */}
          <div className="flex items-center justify-end">
            <span className="text-xs text-gray-400 dark:text-pab-dark-subtle-text">
              {charCount}/{bioMaxLength}
            </span>
          </div>

          {/* Validation messages */}
          {isEditing && (
            <div className="text-xs">
              {charCount < bioMinLength && (
                <Text as="span" className="text-red-500">
                  Please enter at least {bioMinLength} characters for your bio
                </Text>
              )}
              {charCount > bioMaxLength && (
                <Text as="span" className="text-red-500">
                  Please limit your bio to {bioMaxLength} characters
                </Text>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
